import { json, redirect, type LoaderFunctionArgs } from "@remix-run/node";
import { Link } from "@remix-run/react";
import { buttonVariants } from "~/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "~/components/ui/card";
import { CheckIcon } from "lucide-react";
import { trpcServer } from "~/common/trcp";
import { dehydrate, QueryClient, useQuery } from "@tanstack/react-query";
import { ProductWithLocalizedContent } from "~/.server/api/routers/checkout";
import { cn } from "~/lib/utils";
import { authenticateUser } from "~/.server/utils/auth";
import { insertTrackingEvent } from "~/.server/db/tracking";
import { Copy } from "~/components/copy/copy";

// Plan order definition
const PLAN_ORDER = ['free', 'basic', 'premium', 'management-plan'] as const;
type PlanType = typeof PLAN_ORDER[number];

// Plan type detection utility
const getPlanType = (name: string): PlanType => {
  switch (true) {
    case name.toLowerCase().includes('free'):
      return 'free';
    case name.toLowerCase().includes('basic'):
      return 'basic';
    case name.toLowerCase().includes('management'):
      return 'management-plan';
    default:
      return 'premium';
  }
};

// Add loader to fetch products
export async function loader({ request }: LoaderFunctionArgs) {
  // Check if user is authenticated and has an active subscription
  const { user, headers } = await authenticateUser(request);

  const url = new URL(request.url);
  const utmSource = url.searchParams.get('utm_source');
  const utmMedium = url.searchParams.get('utm_medium');
  const utmCampaign = url.searchParams.get('utm_campaign');

  if (user && !user.isSubscribed) {
    insertTrackingEvent({
      userId: user.userId,
      eventType: 'web.subscription_page',
      source: 'web',
      metadata: utmSource ? { utmSource, utmMedium, utmCampaign } : undefined,
    });
  }

  // Allow access if not subscribed OR if subscription is cancelled for renewal
  if (user?.isSubscribed && !user?.cancelAtPeriodEnd) {
    // Fallback: check DB in case session is stale (user cancelled after login)
    const subscriptionStatus = await trpcServer(request).profile.getUserSubscription.query();
    if (!subscriptionStatus.isCancelled) {
      throw redirect('/cuenta/suscripcion', { headers });
    }
    // Session is stale - user has actually cancelled, allow access
  }

  const queryClient = new QueryClient();

  await queryClient.prefetchQuery({
    queryKey: ['products'],
    queryFn: async () =>
      await trpcServer(request).checkout.getProducts.query({ language: 'es' })
  });

  return json({
    dehydratedState: dehydrate(queryClient)
  }, { headers });
}

// Values are copy registry ids (see app/copy/registry.ts), not literal text —
// rendered via <Copy id={...} /> so they're editable from /admin/translations.
const PLAN_FEATURES = {
  free: [
    "suscripcion.plan.free.feature.1",
    "suscripcion.plan.free.feature.2",
  ],
  basic: [
    "suscripcion.plan.basic.feature.1",
    "suscripcion.plan.basic.feature.2",
    "suscripcion.plan.basic.feature.3",
    "suscripcion.plan.basic.feature.4",
    "suscripcion.plan.basic.feature.5",
  ],
  premium: [
    "suscripcion.plan.premium.feature.1",
    "suscripcion.plan.premium.feature.2",
    "suscripcion.plan.premium.feature.3",
    "suscripcion.plan.premium.feature.4",
    "suscripcion.plan.premium.feature.5",
    "suscripcion.plan.premium.feature.6",
    "suscripcion.plan.premium.feature.7",
  ],
  "management-plan": [
    "suscripcion.plan.management.feature.1",
    "suscripcion.plan.management.feature.2",
    "suscripcion.plan.management.feature.3",
    "suscripcion.plan.management.feature.4",
    "suscripcion.plan.management.feature.5",
    "suscripcion.plan.management.feature.6",
    "suscripcion.plan.management.feature.7",
    "suscripcion.plan.management.feature.8",
    "suscripcion.plan.management.feature.9",
  ]
} as const;

interface PlanCardProps {
  product: ProductWithLocalizedContent;
  planType: PlanType;
  features: readonly string[];
}

const getPlanRoute = (planType: PlanType, productId: string) => {
  switch(planType) {
    case 'free':
      return '/cuenta';
    case 'management-plan':
      return '/solicitar-gestionar-espacio';
    default:
      return `/checkout/${productId}`;
  }
};

const PlanCard = ({ product, planType, features }: PlanCardProps) => {
  const isManagementPlan = planType === 'management-plan';
  
  return (
    <Card className={cn(
      "flex flex-col",
      planType === 'premium' && "border-primary shadow-lg scale-105"
    )}>
      <CardHeader>
        <CardTitle className="text-xl font-normal">
          {product.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 space-y-4">
        <div className="text-3xl font-bold">
          {isManagementPlan 
            ? "" 
            : `${(product.price / 100).toFixed(2)}€`
          }
        </div>
        <Link 
          to={getPlanRoute(planType, product.id)}
          className={buttonVariants({ variant: "default" })}
        >
          {isManagementPlan ? <Copy id="suscripcion.cta.contact" /> : <Copy id="suscripcion.cta.select" />}
        </Link>
        <ul className="space-y-2 text-sm">
          {features.map((featureId) => (
            <li key={featureId} className="flex items-center">
              <CheckIcon className="mr-2 h-4 w-4 text-green-500" />
              <Copy id={featureId} />
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter className="text-xs text-gray-500">
        {product.description}
      </CardFooter>
    </Card>
  );
};

export default function Membership() {
  const { data: products } = useQuery<ProductWithLocalizedContent[]>({
    queryKey: ['products']
  });

  if (!products) return null;

  // Sort products based on plan order
  const sortedProducts = [...products].sort((a, b) => {
    const aType = getPlanType(a.name);
    const bType = getPlanType(b.name);
    return PLAN_ORDER.indexOf(aType) - PLAN_ORDER.indexOf(bType);
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        {sortedProducts.map((product) => {
          const planType = getPlanType(product.name);
          const features = PLAN_FEATURES[planType];
          
          return (
            <PlanCard
              key={product.id}
              product={product}
              planType={planType}
              features={features}
            />
          );
        })}
      </div>
    </div>
  );
}
